import * as Yup from "yup";
import { WorkOrderItemType } from "features/WorkOrders/constants/work-order-item.constants";

const BOQ_TYPES = [
  WorkOrderItemType.MATERIAL,
  WorkOrderItemType.LABOR,
  WorkOrderItemType.SERVICE,
] as const;

const itemSchema = Yup.object({
  /** Persisted petty work line id (for material requests); not sent on BOQ save. */
  lineId: Yup.number().nullable().optional(),
  description: Yup.string().trim().required("Description is required"),
  quantity: Yup.number()
    .required("Quantity is required")
    .moreThan(0, "Quantity must be greater than 0"),
  unit: Yup.string().trim().required("Unit is required"),
  rate: Yup.number()
    .typeError("Rate must be a number")
    .min(0, "Rate must be ≥ 0")
    .required("Rate is required"),
  type: Yup.string()
    .oneOf([...BOQ_TYPES], "Invalid line type")
    .required("Type is required"),
});

export function buildSchema() {
  return Yup.object({
    title: Yup.string().required("The title field is required"),
    description: Yup.string().nullable().optional(),
    priorityId: Yup.number().required("The priority field is required"),
    sourceChannelId: Yup.number().nullable().optional(),
    clientId: Yup.mixed<number | string>()
      .required("Client is required")
      .test(
        "client-id-positive",
        "Please select a client",
        (v) =>
          v !== "" && v != null && Number.isFinite(Number(v)) && Number(v) > 0,
      ),
    code: Yup.string()
      .trim()
      .transform((v) => (v ? v.toUpperCase() : v))
      .min(4, "Code must be at least 4 characters")
      .max(50, "Code must be at most 50 characters")
      .matches(
        /^[A-Za-z0-9][A-Za-z0-9\-_/&]*[A-Za-z0-9]$/,
        "Code must start and end with a letter or number, and may only contain letters, numbers, hyphens, underscores, slashes, or &",
      )
      .nullable()
      .optional(),

    plannedStartDate: Yup.string()
      .nullable()
      .optional()
      .test("valid-date", "Enter a valid date", (v) => {
        if (!v) return true;
        return !Number.isNaN(new Date(v).getTime());
      }),

    plannedEndDate: Yup.string()
      .nullable()
      .optional()
      .test("valid-date", "Enter a valid date", (v) => {
        if (!v) return true;
        return !Number.isNaN(new Date(v).getTime());
      })
      .test("end-gte-start", "End date cannot be earlier than start date.", function (v) {
        const start = this.parent.plannedStartDate;
        if (!v || !start) return true;
        return v >= start;
      }),

    items: Yup.array()
      .of(itemSchema)
      .min(1, "Please add at least one line item")
      .required("Please add at least one line item"),

    agreedAmount: Yup.number()
      .typeError("Must be a number")
      .min(0, "Cannot be negative")
      .nullable()
      .optional(),
  });
}

export default buildSchema();
